import { CheckOutlined, IconWrench } from '../../components/icons'
import { isSafePartImageRef } from './AddPartsModal'
import type { PartAnalysisRow, PartAnalysisTone } from './rfq-analysis'

export interface RfqAnalysisOverlayProps {
  readonly rows: readonly PartAnalysisRow[]
  readonly total: number
  readonly doneN: number
  readonly progressPct: number
  readonly moreAbove: boolean
  readonly moreBelow: boolean
}

const TONE_CLASS: Readonly<Record<PartAnalysisTone, string>> = {
  muted: 'rfq-analysis-status--muted',
  success: 'rfq-analysis-status--success',
  warning: 'rfq-analysis-status--warning',
}

/**
 * UID-261 "Preparing your RFQ" entry animation — the presentational overlay
 * (`Fictiv Platform.dc.html` L4072-4102). Mirrors
 * `OrderPlacedCelebration.tsx`'s shape (veil + card, a
 * `prefers-reduced-motion`-aware driving hook) but deliberately
 * does NOT use `role="dialog"`/`aria-modal` — unlike the celebration, the
 * prototype's own overlay here has no click-to-dismiss and is not a true
 * interaction-blocking modal (it's an informational, self-dismissing status
 * overlay); a `role="dialog"` here would also collide with this page's
 * OTHER real dialogs (`AddPartsModal`, `ConfigurePartsModal`,
 * `MoveToNewRfqConfirmModal`, `BulkAddPricedConfirmModal`) in
 * `RfqReviewPage.test.tsx`'s existing `queryByRole('dialog')` assertions.
 * The visible "N of M parts ready" caption doubles as the required polite
 * live region (mirrors `RfqReviewPage`'s own existing
 * `<p aria-live="polite">` pattern).
 *
 * UID-544 / ADR-0166 — there is deliberately NO dismiss affordance. The
 * design's overlay has none (`.dc.html` L4072-4102; only the order-placed
 * animation exposes an `onSkip`, L5118/L10373), and ADR-0089 D5's original
 * reason for adding a "Skip animation" button — escaping a wait that could
 * run to ~13s on a large bundle — no longer exists now that the whole run is
 * bounded by `ANALYSIS_BUDGET_MS` (2s). The polite live region and the
 * reduced-motion path remain the accessible surface.
 */
export function RfqAnalysisOverlay({
  rows,
  total,
  doneN,
  progressPct,
  moreAbove,
  moreBelow,
}: RfqAnalysisOverlayProps) {
  return (
    <div className="rfq-analysis-veil">
      <div className="rfq-analysis-card">
        <div className="rfq-analysis-header">
          <span className="rfq-analysis-header-icon" aria-hidden="true">
            <span
              className="spinner rfq-analysis-spinner rfq-analysis-spinner--brand"
              style={{ width: 22, height: 22 }}
            />
          </span>
          <div className="rfq-analysis-header-copy">
            <h2 className="rfq-analysis-title">Preparing your RFQ</h2>
            <p className="rfq-analysis-subtitle">
              Reading drawings, auto-configuring what we can, and pricing each part.
            </p>
          </div>
        </div>

        <div className="rfq-analysis-progress-track" aria-hidden="true">
          <div className="rfq-analysis-progress-fill" style={{ width: `${progressPct}%` }} />
        </div>
        <p className="rfq-analysis-progress-caption" aria-live="polite">
          {doneN} of {total} parts ready
        </p>

        <div className="rfq-analysis-rows-wrap">
          <div className="rfq-analysis-rows">
            {rows.map((row) => (
              <div key={row.id} className="rfq-analysis-row" style={{ opacity: row.rowOpacity }}>
                <span
                  className="rfq-analysis-thumb"
                  aria-hidden="true"
                  style={
                    isSafePartImageRef(row.imageRef)
                      ? { backgroundImage: `url("${row.imageRef.replace(/["\\]/g, '\\$&')}")` }
                      : undefined
                  }
                />
                <span className="rfq-analysis-row-body">
                  <span className="rfq-analysis-row-name" title={row.name}>
                    {row.name}
                  </span>
                  <span className={`rfq-analysis-row-status ${TONE_CLASS[row.tone]}`}>
                    {row.label}
                  </span>
                </span>
                <span className="rfq-analysis-row-icon" aria-hidden="true">
                  {row.icon === 'spin' && (
                    <span
                      className="spinner rfq-analysis-spinner rfq-analysis-spinner--progress"
                      style={{ width: 16, height: 16 }}
                    />
                  )}
                  {row.icon === 'check' && (
                    <span className="rfq-analysis-icon-circle rfq-analysis-icon-circle--success">
                      <CheckOutlined style={{ fontSize: 14 }} />
                    </span>
                  )}
                  {row.icon === 'wrench' && (
                    <span className="rfq-analysis-icon-circle rfq-analysis-icon-circle--warning">
                      <IconWrench size={13} />
                    </span>
                  )}
                  {row.icon === 'dot' && <span className="rfq-analysis-dot" />}
                </span>
              </div>
            ))}
          </div>
          {moreAbove && (
            <div className="rfq-analysis-fade rfq-analysis-fade--top" aria-hidden="true" />
          )}
          {moreBelow && (
            <div className="rfq-analysis-fade rfq-analysis-fade--bottom" aria-hidden="true" />
          )}
        </div>

        <p className="rfq-analysis-footer">
          Parts with a drawing are auto-configured and priced. The rest just need a quick
          configuration from you.
        </p>
      </div>
    </div>
  )
}
